import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkle, Lightning, X, FloppyDisk, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_INFO = {
  seo: {
    label: 'Full SEO',
    icon: Sparkle,
    color: 'bg-emerald-500/10 text-emerald-700 border-emerald-300 hover:bg-emerald-500/20',
    desc: 'Rik AI-tekst med H2, FAQ og interne lenker. Bruk for høyverdige termer som blir landingssider i Google (leverandør, fiber, designer).',
  },
  seo_short: {
    label: 'Kort SEO',
    icon: Lightning,
    color: 'bg-blue-500/10 text-blue-700 border-blue-300 hover:bg-blue-500/20',
    desc: '~80 ord, faktabasert, ingen FAQ. For nyttige men mindre kommersielle termer (pinnetykkelse, struktur, klestype).',
  },
  skip: {
    label: 'Hopp over',
    icon: X,
    color: 'bg-muted text-muted-foreground border-border hover:bg-muted/80',
    desc: 'Ingen AI-tekst genereres. Bruk for varianter/filtre uten søkeintensjon (fargekoder, størrelse, broderi-farge).',
  },
};

export default function AttributeTierTab({ getHeaders }) {
  const [attrs, setAttrs] = useState([]);
  const [tierMap, setTierMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/autoguard/attribute-tiers`, { headers: getHeaders() });
      setAttrs(data.attributes || []);
      const m = {};
      (data.attributes || []).forEach((a) => { m[a.id] = a.tier; });
      setTierMap(m);
    } catch (err) {
      toast.error('Kunne ikke hente attributter');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const setTier = (id, tier) => {
    setTierMap((prev) => ({ ...prev, [id]: tier }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const stringMap = {};
      Object.entries(tierMap).forEach(([k, v]) => { stringMap[String(k)] = v; });
      await axios.put(`${API}/api/autoguard/settings`, { attribute_tiers: stringMap }, { headers: getHeaders() });
      toast.success('Indeks-policy lagret');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lagring feilet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-32 bg-muted animate-pulse rounded-md" />;

  // Group by tier for stats
  const counts = { seo: 0, seo_short: 0, skip: 0 };
  let totalTerms = 0;
  let activeTerms = 0;
  attrs.forEach((a) => {
    counts[tierMap[a.id]] = (counts[tierMap[a.id]] || 0) + 1;
    totalTerms += a.term_count;
    if (tierMap[a.id] !== 'skip') activeTerms += a.term_count;
  });

  return (
    <Card data-testid="attribute-tier-tab">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-3">
          <span>Indeks-policy per attributt</span>
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-tiers-btn">
            <FloppyDisk size={14} weight="bold" />
            {saving ? 'Lagrer...' : 'Lagre policy'}
          </Button>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Velg hvor grundig hver attributt skal SEO-optimaliseres. Hopp over fargekoder/størrelser for å spare AI-tokens og unngå indeks-støy i Google.
        </p>

        {/* Why-this-matters info box */}
        <div className="mt-4 p-4 rounded-lg border border-amber-300/50 bg-amber-50/60 dark:bg-amber-500/5 dark:border-amber-500/30" data-testid="tier-why-box">
          <div className="flex items-start gap-3">
            <Info size={20} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-2 text-xs leading-relaxed">
              <p className="font-bold text-foreground text-sm">Hvorfor ikke kjøre full SEO på alle attributter?</p>
              <p className="text-muted-foreground">
                Hver attributt-term blir til en egen URL i WooCommerce. Med {totalTerms} termer ville
                full indeksering bety {totalTerms} ekstra sider — de fleste med tynt eller duplisert
                innhold (fargekoder, varianter, broderi-farger). Dette utløser{' '}
                <strong className="text-foreground">"index bloat"</strong> i Google: kvalitetssignalet
                til hele domenet svekkes, og virkelig viktige sider (produkter, leverandørsider, kategorier)
                rangeres lavere.
              </p>
              <p className="text-muted-foreground">
                Strategien er: <strong className="text-foreground">Full SEO</strong> kun for attributter som
                folk faktisk søker etter (Garn, Leverandør, Fiber, Designer) — disse blir landingssider.{' '}
                <strong className="text-foreground">Kort SEO</strong> for nyttige filtre uten kommersiell søkeintensjon
                (pinnetykkelse, strikkefasthet). <strong className="text-foreground">Hopp over</strong> for
                rene varianter (fargekoder, størrelser) — de skal eksistere som filtre, men ikke som indekserte sider.
              </p>
              <p className="text-foreground font-medium">
                Med policyen under sparer du AI-tokens på {totalTerms - activeTerms} termer ({Math.round((1 - activeTerms / Math.max(totalTerms, 1)) * 100)}% reduksjon)
                — og Google forstår tydeligere hvilke sider som er verdt å rangere.
              </p>
            </div>
          </div>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <StatPill label="Full SEO" count={counts.seo} color="text-emerald-700" />
          <StatPill label="Kort SEO" count={counts.seo_short} color="text-blue-700" />
          <StatPill label="Hoppes over" count={counts.skip} color="text-muted-foreground" />
          <StatPill label={`Aktiverte termer / totalt`} count={`${activeTerms} / ${totalTerms}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Object.entries(TIER_INFO).map(([tierKey, info]) => {
            const matching = attrs.filter((a) => tierMap[a.id] === tierKey);
            if (matching.length === 0) return null;
            const Icon = info.icon;
            return (
              <div key={tierKey}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} weight="bold" />
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wide">{info.label}</h3>
                  <Badge variant="secondary" className="text-xs">{matching.length} attributter</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{info.desc}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                  {matching.map((a) => (
                    <AttrRow key={a.id} attr={a} currentTier={tierMap[a.id]} onChange={(t) => setTier(a.id, t)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StatPill({ label, count, color = '' }) {
  return (
    <div className="bg-muted/30 rounded p-2 border border-border/50">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`font-heading text-lg font-bold ${color}`}>{count}</p>
    </div>
  );
}

function AttrRow({ attr, currentTier, onChange }) {
  return (
    <div className="flex items-center justify-between gap-2 p-2 bg-card border border-border rounded">
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate" title={attr.name}>{attr.name}</p>
        <p className="text-[10px] text-muted-foreground">{attr.term_count} termer</p>
      </div>
      <div className="flex gap-1 shrink-0">
        {Object.keys(TIER_INFO).map((tier) => {
          const Icon = TIER_INFO[tier].icon;
          const active = currentTier === tier;
          return (
            <button
              key={tier}
              onClick={() => onChange(tier)}
              type="button"
              className={`px-1.5 py-1 rounded text-[10px] flex items-center gap-1 border transition-colors ${
                active ? TIER_INFO[tier].color + ' font-bold' : 'border-border text-muted-foreground hover:bg-muted'
              }`}
              title={TIER_INFO[tier].label}
              data-testid={`attr-${attr.id}-${tier}`}
            >
              <Icon size={10} weight="bold" />
              {tier === 'seo' ? 'Full' : tier === 'seo_short' ? 'Kort' : 'Hopp'}
            </button>
          );
        })}
      </div>
    </div>
  );
}
